import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Select, MenuItem, 
  InputLabel, FormControl, Alert, Dialog, DialogTitle, 
    DialogContent, DialogActions,  } from '@mui/material';
import { apiRequest } from '../../../utils/fetch';
import { UserInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import userRoleStore from '../../../store/userRolesStore';

const initialUser: UserInterface = {
  username: '',
  password: '',
  name: '',
  description: '',
  email: '',
  phone: '',
  address: '',
  userRoleId: undefined,
  availableTokens: 0,
  active: true,
};

const UsersAdminAddUserPage: React.FC = () => {
  const [user, setUser] = useState<UserInterface>(initialUser);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState<'success' | 'error'>('success');  

  const handleChange = (field: keyof UserInterface, value: any) => {
    setUser(u => ({ ...u, [field]: value }));
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setSuccess(null);
  try {
    const result = await apiRequest<GenericResponseInterface<UserInterface>>('/users/insert', 'POST', user);
    if (result.status) {
      setDialogMessage(result.message || 'User created successfully!');
      setDialogType('success');
      setDialogOpen(true);
      setUser(initialUser);
    } else {
      setDialogMessage(result.message || 'Failed to create user');
      setDialogType('error');
      setDialogOpen(true);
    }
  } catch (err: any) {
    setDialogMessage(err.message || 'Failed to create user');
    setDialogType('error');
    setDialogOpen(true);
  }
};

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New User</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Username"
          value={user.username}
          onChange={e => handleChange('username', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Password"
          type="password"
          value={user.password}
          onChange={e => handleChange('password', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Name"
          value={user.name}
          onChange={e => handleChange('name', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Description"
          value={user.description ?? ''}
          onChange={e => handleChange('description', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Email"
          value={user.email}
          onChange={e => handleChange('email', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Phone"
          value={user.phone ?? ''}
          onChange={e => handleChange('phone', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Address"
          value={user.address}
          onChange={e => handleChange('address', e.target.value)}
          fullWidth
          margin="dense"
          required
        />

        <FormControl fullWidth margin="dense" required>
          <InputLabel id="user-role-label">User Role</InputLabel>
          <Select
            labelId="user-role-label"
            value={user.userRoleId ?? ''}
            label="User Role"
            onChange={e => handleChange('userRoleId', Number(e.target.value))}
          >
            {userRoleStore.userRoles.map(role => (
              <MenuItem key={role.id} value={role.id}>{role.roleName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Available Tokens"
          type="number"
          value={user.availableTokens ?? ''}
          onChange={e => handleChange('availableTokens', Number(e.target.value))}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={user.active ? 'yes' : 'no'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create User
          </Button>



        </Box>
      </form>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{dialogType === 'success' ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

export default UsersAdminAddUserPage;